import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { LocationWithOption } from "@/types/book";
import { Course } from "@/types/course";
import { Role } from "@/types/user";
import { FormType } from "@/types/form";
import env from "@/utils/env";
export { socketService } from './socket';
import {  getToken, removeToken, setToken } from '@/utils/index';

export const baseUrl = env.baseUrl;

const headers: any = {
    "Content-Type": "application/json",
};

export const apiClient = axios.create({
    baseURL: baseUrl,
    headers
});


apiClient.interceptors.response.use(
    (response) => {
        const { status, data } = response;
        if ([200, 201].includes(status)) {
            toast.success(data.message, { autoClose: 1000 });
        }
        return response;
    },
    (error) => {
        if (error.response) {
            const { status, data } = error.response;
            switch (status) {
                case 401: {
                    const hasToken = getToken();
                    if (hasToken) {
                        removeToken();
                    }
                    break;
                }
                case 403:
                case 404:
                case 405:
                case 500:
                    toast.error(data.message, { autoClose: 1000 });
                    break;
                default:
                    break;
            }
        }
        return Promise.reject(error);
    }
);

export const login = async (
    email: string,
    password: string,
    remember: boolean
) => {
    const response = await apiClient.post(`/login`, {
        email,
        password,
    });
    const {
        access_token,
        user: { id },
    } = response.data;
    if (access_token && remember) {
        setToken(access_token)
        localStorage.setItem("cacuserid", id);
    }
    if (access_token && !remember) {
        setToken(access_token)
        sessionStorage.setItem("cacuserid", id);
    }
    return response.data;
};
export const getRoleList = async () => {
    const response = await apiClient.get<Role[]>("/roles/list");
    return response.data;

};

export const register = async (
    name: string,
    email: string,
    password: string,
    confirm_password: string,
    mobile_number: number,
    role: string
) => {
    const response = await apiClient.post(`/register`, {
        name,
        email,
        password,
        confirm_password,
        mobile_number,
        role,
    });
    return response;
};
export const logout = async () => {
    return await apiClient.post(`/logout`);
};

export const changePassword = async (data: {
    current_password: string;
    password: string;
    password_confirmation: string;
}) => {
    const response = await apiClient.post(`/change-password`, data);
    return response.data;
};

export const uploadUserProfileImage = async (file: File) => {
    const formData = new FormData();
    formData.append('profile_image', file);
    const response = await apiClient.post('/profile/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as { message: string; profile_image: string };
};

export const getUserProfile = async () => {
    const response = await apiClient.get('/user-profile');
    return response.data;
};

export const updateUserProfile = async (data: {
    name: string;
    gender: string;
    dob: string;
    mobile: string;
    phone: string;
    locations_id?: number[];
    categories_id?: number[];
    courses_id?: number[];
}) => {
    const response = await apiClient.post('/profile', data);
    return response.data;
};

export const getUsers = async () => {
    try {
        const response = await apiClient.get(`/users`, {
            params: {
                pagination: 1000,
            }
        });
        return response.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        } else {
            return Promise.reject(error);
        }
    }
};
export const getCoursesList = async () => {
    const response = await apiClient.get<Course[]>(`/course-list`);
    return response.data;
};
export const getCourses = async () => {
    const response = await apiClient.get(`/course?pagination=1000`);
    return response.data;
};
export const getCourseCategories = async () => {
    const response = await apiClient.get(`/category?pagination=1000`);
    return response.data;
};
export const getCourseBasedOnLococationsCategories = async (data: any) => {
    const response = await apiClient.post(
        `/course-based-on-locations-categories`,
        data
    );
    return response.data;
};
export const getLocation = async () => {
    const response = await apiClient.get<LocationWithOption[]>(`/location`);
    return response.data;
};
export const getNonStudentUsers = async () => {
    try {
        const response = await apiClient.get(`/non-student-users?pagination=1000`);
        return response.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        } else {
            return Promise.reject(error);
        }
    }
};
export const getChats = () => {
    return apiClient.get('/chat/active-chats');
};

export const createChat = (data: {
    department_role: string;
    subject: string;
    message: string;
}) => {
    return apiClient.post('/chat/start', data);
};

export const getChatMessages = (chatId: number) => {
    return apiClient.get(`/chat/${chatId}/messages`);
};

export const sendChatMessage = (chatId: number, message: string) => {
    return apiClient.post(`/chat/${chatId}/message`, { message });
};

export const closeChat = (chatId: number) => {
    return apiClient.post(`/chat/${chatId}/close`);
};

export const getDepartments = () => {
    return apiClient.get('/chat/departments');
};
export const getDepartmentRoles = () => {
    return apiClient.get('/roles', {
        params: {
            permission: 'menu-chat',
            exclude: ['Student', 'Trainer', 'Agent']
        }
    });
};
export const userEmailVerify = async ({ id, verify }: any) => {
    const response = await apiClient.post(`/users/${id}/verify`, { verify });
    return response.data;
};

export const getTickets = async (params?: any) => {
    const response = await apiClient.get('/tickets', { params });
    return response.data;
};

export const createTicket = async (data: any) => {
    const response = await apiClient.post('/tickets', {
        ...data,
        department_role_id: data.department_role_id
    });
    return response.data;
};

export const getTicketDetails = async (id: number) => {
    const response = await apiClient.get(`/tickets/${id}`);
    return response.data;
};

export const updateTicket = async (id: number, data: any) => {
    const response = await apiClient.put(`/tickets/${id}`, data);
    return response.data;
};

export const assignTicket = async (id: number, user_ids: number[]) => {
    const response = await apiClient.post(`/tickets/${id}/assign`, { user_ids });
    return response.data;
};
export const deleteTicket = async (id: number) => {
    const response = await apiClient.delete(`/tickets/${id}`);
    return response.data;
};

export const removeTicketAssignment = async (ticketId: number, userId: number) => {
    const response = await apiClient.delete(`/tickets/${ticketId}/assignments/${userId}`);
    return response.data;
};
export const closeTicket = async (id: number, data: { resolution_notes: string }) => {
    const response = await apiClient.post(`/tickets/${id}/close`, data);
    return response.data;
};

export const reopenTicket = async (id: number) => {
    const response = await apiClient.post(`/tickets/${id}/reopen`);
    return response.data;
};

export const getTicketReports = async (params?: any) => {
    const response = await apiClient.get('/tickets/reports', { params });
    return response.data;
};

// Ticket Messages APIs
export const addTicketMessage = async (ticketId: number, data: FormData) => {
    const response = await apiClient.post(`/tickets/${ticketId}/messages`, data, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const getTicketMessageAttachment = async (messageId: number, fileName: string) => {

    const response = await apiClient.get(`/tickets/messages/${messageId}/attachments/${fileName}`, {
        responseType: 'blob'
    });
    return response.data;
};

export const downloadTicketMessageAttachment = async (messageId: number, fileName: string) => {
    const response = await apiClient.get(`/tickets/messages/${messageId}/attachments/${fileName}/download`, {
        responseType: 'blob'
    });
    return response.data;
};

export const deleteTicketMessageAttachment = async (messageId: number, fileName: string) => {
    const response = await apiClient.delete(`/tickets/messages/${messageId}/attachments/${fileName}`);
    return response.data;
};

export const getTicketMessages = async (ticketId: number) => {
    const response = await apiClient.get(`/tickets/${ticketId}/messages`);
    return response.data;
};

export const getCoursesFromLocationId = async (id: any) => {
    const response = await apiClient.get(`/location-courses/${id}`);
    return response.data;
};

export const getCoursesFromLocation = async (courseId?: string) => {
    const locationId = courseId ? courseId : localStorage.getItem("courseLocationId");
    if (locationId) {
        const response = await apiClient.get(
            `/parent-category-course-location/${locationId}`
        );
        return response.data;
    }
    return Promise.reject({
        error: "Please select the course location first",
    });
};

export const toggleChatAvailability = async (available: boolean) => {
    const response = await apiClient.post('/user/toggle-chat-availability', { available });
    return response.data;
};

//chat
export const chatEndpoints = {

    getAvailableStaffRoleGuests: () =>
        apiClient.get('/chat/guest/available-staff'),

    startGuestChat: (data: {
        name: string;
        email: string;
        phone: string;
        subject: string;
        staff_role: string;
    }) => apiClient.post('/chat/guest/start', data),

    getGuestChatHistory: (chatId: string, email: string) =>
        apiClient.get(`/chat/guest/${chatId}/history`, { params: { email } }),

    sendGuestMessage: (chatId: string, content: any, guestEmail: string) =>
        apiClient.post(`/chat/guest/${chatId}/message`, { content, guest_email: guestEmail }),

    endGuestChat: (chatId: string, guestEmail: string) =>
        apiClient.post(`/chat/guest/${chatId}/end`, { guest_email: guestEmail }),
    // User chat operations
    startUserChat: (data: {
        staff_role: string,
        subject: string
    }) => apiClient.post('/chat/user/start', data),

    // Chat lists
    getActiveChats: () => apiClient.get('/chat/active'),
    getUnassignedChats: () => apiClient.get('/chat/unassigned'),
    getUnreadCount: () => apiClient.get('/chat/unread-count'),

    // Chat operations
    getChatHistory: (chatId: string) => apiClient.get(`/chat/${chatId}/history`),
    sendMessage: (chatId: string, content: any) => apiClient.post(`/chat/${chatId}/message`, { content }),
    markMessagesAsRead: (chatId: string) => apiClient.post(`/chat/${chatId}/read`),

    // Support staff operations
    joinSupportStaff: (chatId: string) => apiClient.post(`/chat/${chatId}/join-support-staff`),
    leaveSupportStaff: (chatId: string) => apiClient.post(`/chat/${chatId}/leave-support-staff`),
    rejoinSupportStaff: (chatId: string) => apiClient.post(`/chat/${chatId}/rejoin-support-staff`),
    addSupportStaff: (chatId: string, staffId: number) => apiClient.post(`/chat/${chatId}/add-support-staff`, { staff_id: staffId }),

    // Chat status
    endChat: (chatId: string) => apiClient.post(`/chat/${chatId}/end`),
    transferChat: (chatId: string, staffId: number) => apiClient.post(`/chat/${chatId}/transfer`, { staff_id: staffId }),

    // Staff availability
    getAvailableStaffRoles: () => apiClient.get('/chat/staff-roles'),
    getAvailableStaff: (role: string) => apiClient.get(`/chat/available-staff/${role}`)
};


export const getCoursesName = async (data: any) => {
    const locationId = data.queryKey[1];
    try {
        if (locationId) {
            const response = await apiClient.get(`/location-courses/${locationId}`);
            return response.data;
        } else {
            return Promise.reject({
                error: "Please select the course location first",
            });
        }
    } catch (error: any) {
        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        } else {
            return Promise.reject(error);
        }
    }
};

export const getFormTypes = async (): Promise<FormType[]> => {
    const response = await apiClient.get('/form-type');
    return response.data;
};

export const getForms = async (formId: any) => {
    if (formId) {
        const response = await apiClient.get(
            `/form?pagination=10000&form_type=${formId}`
        );
        return response.data;
    } else {
        return Promise.reject({
            error: "Please select form type first",
        });
    }
};

export const getIndividualForms = async (formId: any) => {
    const response = await apiClient.get(`/form/${formId}`);
    return response.data;
};

export const getFormDetails = async (formId: any) => {
    try {
        if (formId) {
            const response = await apiClient.get(`/form/${formId}`);
            return response.data;
        } else {
            return Promise.reject({
                error: "Please select form first",
            });
        }
    } catch (error: any) {
        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        } else {
            return Promise.reject(error);
        }
    }
};

export const getAQFLevels = async () => {
    const response = await apiClient.get('/courses/aqf-levels');
    return response.data.aqf_levels;
};

export const getAQFLevelsByFilter = async (categoryId?: string) => {
    const params = categoryId ? { category_id: categoryId } : {};
    const response = await apiClient.get('/courses/aqf-levels/filter', { params });
    return response.data.aqf_levels;
};

interface SubmitForm {
    values: any;
    formTypeId: string;
    submit: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    studentId?: string;
    course_location_id?: string;
    courseId?: string;
    email?: string;
    appliedDate?: string;
    profile_img?: File;
    phone?: string;
    version?: string;
    isAgent?: boolean;
    agentname?: string;
    agent_email?: string;
    step?: string;
    [key: string]: any;
}
export const submitForm = async (formValues: SubmitForm) => {
    const { values: answer, } = formValues;
    try {
        const formData = new FormData();

        if (formValues.profile_img) formData.append("profile_image", formValues.profile_img);
        if (formValues.firstName) formData.append("form[firstname]", formValues.firstName);
        if (formValues.middleName) formData.append("form[middlename]", formValues.middleName);
        if (formValues.lastName) formData.append("form[lastname]", formValues.lastName);
        if (formValues.studentId) formData.append("form[student_id]", formValues.studentId);
        if (formValues.course_location_id) formData.append("form[course_location_id]", formValues.course_location_id);
        if (formValues.courseId) formData.append("form[course_id]", formValues.courseId);
        if (formValues.email) formData.append("form[email]", formValues.email);
        if (formValues.phone) formData.append("form[phone]", formValues.phone);
        if (formValues.appliedDate) formData.append("form[applied_date]", formValues.appliedDate);
        if (formValues.isAgent) formData.append("form[isAgent]", JSON.stringify(formValues.isAgent));
        if (formValues.agentname) formData.append("form[agentname]", formValues.agentname);
        if (formValues.agent_email) formData.append("form[agent_email]", formValues.agent_email);
        if (formValues.step) formData.append("form[step]", formValues.step);
        
        formData.append("form[form_type_id]", formValues.formTypeId);
        formData.append("form[submit]", formValues.submit || "false");
        formData.append("form[version]", formValues.version || "v5");
        formData.append("form[answer]", JSON.stringify(answer));

        const response = await apiClient.post('/form', formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },

        });
        return response.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        } else {
            return Promise.reject(error);
        }
    }
};

export const updateForm = async ({
    values,
    formTypeId,
    signature,
    signature2,
    signature3,
    submit,
    firstName,
    middleName,
    lastName,
    studentId,
    course_location_id,
    courseId,
    email,
    profile_img,
    phone,
    passport1,
   // passport2,
    education1,
    education2,
    language1,
    oshc,
    engCOnvtedDoc,
    finances1,
    // finances2,
    language2,
    verified,
    formEditId,
    version,
    isAgent = false,
    agentname = '',
    agent_email = '',
    step
}: {
    values: any;
    formTypeId: string;
    signature?: any;
    signature2?: any;
    signature3?: any;
    submit?: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    studentId?: string;
    course_location_id?: string;
    courseId?: string;
    email?: string;
    profile_img?: File;
    phone?: string;
    passport1?: File[];
    passport2?: File;
    education1?: File[];
    education2?: File[];
    language1?: File[];
    oshc?: File[];
    engCOnvtedDoc?: File[];
    finances1?: File[];
    // finances2?: File[];
    language2?: File[];
    verified?: any;
    formEditId?: any;
    version?: string;
    isAgent?: boolean;
    agentname?: string;
    agent_email?: string;
    step?: string;
}) => {
    let form_id = sessionStorage?.getItem("enrollment_form_id");
    if (!form_id) {
        form_id = sessionStorage?.getItem("application_form_id");
        if (!form_id) {
            form_id = sessionStorage?.getItem("variation_form_id");
        }
    }
    try {
        const formData = new FormData();
        formData.append("_method", "put");
        if (profile_img) formData.append("profile_image", profile_img);
        if (signature) formData.append("signature[signature1]", signature);
        if (signature2) formData.append("signature[signature2]", signature2);
        if (signature3) formData.append("signature[signature3]", signature3);
        if (version) formData.append("form[version]", version);

        if (firstName) formData.append("form[firstname]", firstName);

        if (middleName) formData.append("form[middlename]", middleName);

        if (lastName) formData.append("form[lastname]", lastName);

        if (course_location_id)
            formData.append("form[course_location_id]", course_location_id);

        if (email) formData.append("form[email]", email);

        if (phone) formData.append("form[phone]", phone);

        if (studentId) formData.append("form[student_id]", studentId);

        if (courseId) formData.append("form[course_id]", courseId);
        if (isAgent) formData.append("form[isAgent]", JSON.stringify(isAgent));
        if (agentname) formData.append("form[agentname]", agentname);
        if (agent_email) formData.append("form[agent_email]", agent_email);
        if (step) { formData.append("form[step]", step); }

        if (passport1 && passport1.length > 0) {
            [...passport1].forEach((el, index) => {
                formData.append(`passport[${index}]`, el);
            });
        }
        // if (passport1) formData.append("passport[passport1]", passport1);

        // if (passport2) formData.append("passport[passport2]", passport2);
        if (verified) formData.append("verified", verified);

        if (education1 && education1.length > 0) {
            [...education1].forEach((el, index) => {
                formData.append(`education[${index}]`, el);
            });
        }
        if (education2 && education2.length > 0) {
            [...education2].forEach((el, index) => {
                formData.append(`ceducation[${index}]`, el); //ceducation[]
            });
        }
        if (language1 && language1.length > 0) {
            [...language1].forEach((el, index) => {
                formData.append(`language[${index}]`, el);
            });
        }
        if (oshc && oshc.length > 0) {
            [...oshc].forEach((el, index) => {
                formData.append(`other[${index}]`, el);
            });
        }
        if (engCOnvtedDoc && engCOnvtedDoc.length > 0) {
            [...engCOnvtedDoc].forEach((el, index) => {
                formData.append(`engdoc[${index}]`, el); //engdoc[]
            });
        }
        if (finances1 && finances1.length > 0) {
            [...finances1].forEach((el, index) => {
                formData.append(`finance[${index}]`, el);
            });
        }

        if (language2 && language2.length > 0) {
            [...language2].forEach((el, index) => {
                formData.append(`instructingdoc[${index}]`, el); //instructingdoc[]
            });
        }

        formData.append("form[form_type_id]", formTypeId);
        if (submit) formData.append("form[submit]", submit);
        formData.append("form[answer]", JSON.stringify(values));

        let response;
        if (formEditId) {
            response = await apiClient.post(`/form/${formEditId}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
        } else {
            response = await apiClient.post(`/form/${form_id}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
        }

        return response?.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        } else {
            return Promise.reject(error);
        }
    }
};

export const verifyForm = async (formId: any) => {
    try {
        const response = await apiClient.get(`/form-verify/${formId}`);

        return response?.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        } else {
            return Promise.reject(error);
        }
    }
};

export const UnverifyForm = async (formId: any) => {
    try {
        const response = await apiClient.get(`/form-unverify/${formId}`);

        return response?.data;
    } catch (error: any) {
        if (error?.response?.data) {
            return Promise.reject(error.response.data);
        } else {
            return Promise.reject(error);
        }
    }
};

export const getQueryData = async (data: any) => {
    const api = data?.queryKey[0];
    const query = data?.queryKey[1];
    const orderBy = data?.queryKey[2];
    const sort = data?.queryKey[3];
    const page = data?.queryKey[4];
    const pagination = data?.queryKey[5];
    const form_type = data?.queryKey[6];
    const role = data?.queryKey[7];
    const location_id = data?.queryKey[8];
    const course_id = data?.queryKey[9];
    const booking_datetime = data?.queryKey[10];

    let URL = `/${api}?&page=${page}&pagination=${pagination}`;
    if (!query && !orderBy && !sort) {
        URL = `/${api}?&page=${page}&pagination=${pagination}`;
    }
    if (query) {
        URL = `/${api}?search=${query}&pagination=${pagination}`;
    }
    if ((orderBy !== "" || sort !== "") && !query) {
        if (sort === "") {
            URL = `/${api}?&page=${page}&order_by=${orderBy}&pagination=${pagination}`;
        } else {
            URL = `/${api}?&order=${sort}&page=${page}&order_by=${orderBy}&pagination=${pagination}`;
        }
    }
    if (form_type) {
        URL = `/${api}?search=${query}&order=${sort}&page=${page}&order_by=${orderBy}&pagination=${pagination}&form_type=${form_type}`;
    } else if (role) {
        URL = `/${api}?search=${query}&order=${sort}&page=${page}&order_by=${orderBy}&pagination=${pagination}&role=${role}`;
    } else {
        URL = `/${api}?search=${query}&order=${sort}&page=${page}&order_by=${orderBy}&pagination=${pagination}`;
    }
    if (location_id) {
        URL = URL + `&location_id=${location_id}`;
    }
    if (course_id) {
        URL += `&course_id=${course_id}`;
    }
    if (booking_datetime) {
        URL += `&booking_datetime=${booking_datetime}`;
    }
    const response = await apiClient.get(URL);
    return response.data;
};

export const axiosInterceptor = axios.interceptors.response.use(
    function (response) {
        const { status, data } = response;
        if (status == 201 || status == 200) {
            toast.success(data.message, { autoClose: 1000 });
        }
        return response;
    },
    function (error) {
        if (!error.response) {
            return Promise.reject(error);
        }
        const { status, data } = error.response;
        if (status == 403) {
            toast.error(data.message, { autoClose: 1000 });
        }
        if (status == 500) {
            toast.error(data.message, { autoClose: 1000 });
        }
        if (status == 405) {
            toast.error(data.message, { autoClose: 1000 });
        }
        return Promise.reject(error);
    }
);

apiClient.interceptors.request.use(
    async (config: any) => {
        const access_token = getToken()

        if (access_token) {
            config.headers.Authorization = "Bearer " + access_token;
        }
        if (config.data instanceof FormData) {
            delete config.headers["Content-Type"];
            delete config.headers["content-type"];
        }
        if (config.method == "delete") {
            const modal = Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#1ea432e6",
                cancelButtonColor: "#9F1853",
                confirmButtonText: "Yes",
                cancelButtonText: "No",
                reverseButtons: true,
            });
            const { isConfirmed } = await modal;
            if (isConfirmed) {
                // Swal.fire(
                // 	'Deleted!',
                // 	'Your file has been deleted.',
                // 	'success'
                // )
                return config;
            }
            // else if (
            //     /* Read more about handling dismissals below */
            //     dismiss === Swal.DismissReason.cancel
            // ) {
            //     Swal.fire(
            //         'Cancelled',
            //         'Your imaginary file is safe :)',
            //         'error'
            //     )
            // }
        } else {
            return config;
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Google authentication API methods
export const googleLogin = async (token: string) => {
    const response = await apiClient.post('/auth/google/token', { token });
    const { access_token, user } = response.data;

    if (access_token) {
        localStorage.setItem("cactoken", access_token);
        localStorage.setItem("cacuserid", user.id);
        setToken(access_token);
    }

    return response.data;
};

export const googleRedirect = () => {
    window.location.href = `${baseUrl}/auth/google`;
};
